<template>
    <div
        v-if="isAppLoaded && canStart"
        :style="themStyles"
    >
        <TTerminal
            @onMainContainerClick="setAppActive"
        >
            <template #overlay>
                <div
                    class="main__overlay"
                    v-if="isOverlay"
                    @click="isOverlay = false"
                />
                <OLoadingOverlay 
                    v-if="isLoading"
                />
            </template>
            <template #view>

                <keep-alive v-if="isPagesKeepAliveCacheEnabled">
                    <router-view :key="$route.fullPath" />
                </keep-alive>
                <template v-else>
                    <router-view :key="$route.fullPath"/>
                </template>

                <template v-if="computedGettersPreloadingTrigger"/>

                <router-view name="guide" />
            </template>
            <template #modal>
                <OModalBase
                    v-if="modalOrganism"
                    @onClose="closeModal"
                    :organism="modalOrganism.component"
                    :data="modalOrganism.data"
                    :clickOutside="modalOrganism.clickOutside"
                />
                <component :is="smsModalComponent" />
                <component :is="tilesModalComponent" />
                <component :is="servicesModalComponent" />
                <component :is="infoModalComponent" />
                <template v-for="item in themeModals">
                    <component
                        :is="item"
                        :key="item"
                    />
                </template>
            </template>
            <template #player>
                <OPlayer
                    v-if="player"
                    @onPlayerClick="handlePlayerClick"
                />
            </template>
        </TTerminal>
        <ODebugPanel v-if="shouldShowDebugPanel"/>
    </div>
    <AInitialLoader v-else />
</template>

<script>
import '../../../assets/images/generated-svg';

import {
    mapState,
    mapGetters,
    mapActions,
    mapMutations
} from 'vuex';

import * as Sentry from "@sentry/vue";

import OSmsModal3 from '../../organisms/o-sms-modal-3';
import OPlayer from '../../organisms/o-player/o-player';
import OTilesModal from '../../organisms/o-tiles-modal';
import OInfoModalSK from '../../organisms/o-info-modal-sk';
import OTilesModal3 from '../../organisms/o-tiles-modal-3';
import OTilesModal4 from '../../organisms/o-tiles-modal-4';
import OSmsModalOtg from '../../organisms/o-sms-modal-otg';
import OTilesModalSK from '../../organisms/o-tiles-modal-sk';
import TTerminal from '../../templates/t-terminal/t-termianl';
import OTilesModalOtg from '../../organisms/o-tiles-modal-otg';
import OMobileMapModal from '../../organisms/o-mobile-map-modal';
import OServicesModalSK from '../../organisms/o-services-modal-sk';
import OModalBase from '../../organisms/o-modal-base/o-modal-base';
import OServicesModal from '../../unique-organisms/o-services-modal';
import OServicesModalOtg from '../../organisms/o-services-modal-otg';
import OServicesModalOtg3 from '../../organisms/o-services-modal-otg-3';
import AInitialLoader from '../../atoms/a-initial-loader/a-initial-loader';
import OLoadingOverlay from '../../organisms/o-loading-overlay/o-loading-overlay';
import OOpeningHoursMobilia from '../../unique-organisms/mobilia/o-opening-hours-mobilia';

import canStart from '../../../utils/mixins/can-start';

import { fitToScreen } from '@/utils/helpers/fit-to-screen';
import { SET_GYROSCOPE_STATE } from '@/shared/components/map-viewer/constants/map-commands';

import {
    START_SCREEN,
    GUIDE_SCREEN,
    SEARCH_SCREEN
} from '@/utils/constants/pages';

import {
    EVENTS,
    SHOW_MODAL,
    SHOW_OVERLAY,
    RESTORE_LOCALE,
    SESSION_EXTENDED,
    SESSION_TERMINATED
} from '@/utils/constants/bus-events-constants';
import {
    UI_MAP_READY,
    STOP_VIEW_API,
    UI_MAP_CLICKED,
} from "@/shared/components/map-viewer/constants/map-events";
import {
    RESET_TERMINAL_DATA,
    DELAY_BEFORE_PLAYER_START,
    INTERVAL_CHECK_MAP_DATA_MODIFIED,
    DELAY_BEFORE_CAN_UPDATE_TEMINAL_DATA,
} from '@/utils/constants/dates-time';
import { APP_TYPES } from "@/utils/constants/app-constants";
import ODebugPanel from "@/components/organisms/o-debug-panel/o-debug-panel.vue";

export default {
    name: 'PTerminal',
    mixins: [canStart],
    components: {
        ODebugPanel,
        OPlayer,
        TTerminal,
        OModalBase,
        OSmsModal3,
        OTilesModal,
        OInfoModalSK,
        OTilesModal3,
        OTilesModal4,
        OSmsModalOtg,
        OTilesModalSK,
        OTilesModalOtg,
        AInitialLoader,
        OServicesModal,
        OLoadingOverlay,
        OMobileMapModal,
        OServicesModalSK,
        OServicesModalOtg,
        OServicesModalOtg3,
        OOpeningHoursMobilia,
        OAboutModal: () => import('../../unique-organisms/o-about-modal'),
        OOpeningHoursGranby: () => import('../../unique-organisms/granby/o-opening-hours-granby'),
        OOpeningHoursNordstan: () => import('../../unique-organisms/nordstan/o-opening-hours-nordstan'),
        OOpeningHoursNordstanImage: () => import('../../unique-organisms/nordstan/o-opening-hours-nordstan-image'),
    },
    data() {
        return {
            player: false,
            isOverlay: false,
            modalOrganism: null,
            activeTimeout: null,
            screensaverTimeout: null,
            setAppActiveWithDelayTimeoutId: null,
            screensaverCooldounTimeout: null,
            checkMapObjectsInterval: null,
            mapDataUpdateCheckInterval: null,
            delayUpdateTerminalDataTimeout: null,
            playerCooldownDelayTimeout: null,
        }
    },
    props: {
        terminalCode: {
            type: [String,Number],
            default: null
        },
        mapId: {
            type: String, Number,
            default: null
        }
    },
    computed: {
        ...mapState({
            map: state => state.map,
            isLoading: state => state.isLoading,
            mapLoading: state => state.mapLoading,
            lastModified: state => state.lastModified,
            dataSourceType: state => state.dataSourceType,
            defaultLocale: state => state.map.meta.language,
            canUpdateMapData: state => state.canUpdateMapData,
            moviesListTimeout: state => state.moviesListTimeout,
            unfilteredMapObjects: state => state.unfilteredMapObjects,
            isMapDataUpdateNeeded: state => state.isMapDataUpdateNeeded,
            resetInterval: state => state.map.meta.reset_interval,
            screensaverTimer: state => state.map.meta.screensaver_delay,
            screensaverCooldownDelay: state => state.map.meta.screensaver_cooldown_delay,
        }),
        ...mapGetters([
            'isElo',
            'isAppLoaded',
            'getTerminal',
            'getBrandsAZ',
            'getTenantsAZ',
            'getBrandsRoot',
            'getCategoriesAZ',
            'getActiveLocale',
            'getMapConfigProp',
            'getServiceCategoriesAZ',
            'getTenantCategoriesRoot',
            'getTenantRootCategoryChildren',
        ]),
        isShowFooter() {
            return ![GUIDE_SCREEN, SEARCH_SCREEN].includes(this.$route.name);
        },
        themStyles() {
            return this.getMapConfigProp('theme_styles') || null;
        },
        themeParams() {
            return this.getMapConfigProp('theme_params') || {};
        },
        servicesModalComponent() {
            return this.themeParams?.['serviceModalComponent'] || 'OServicesModal';
        },
        infoModalComponent() {
            return this.themeParams?.['infoModalComponent'] || 'OInfoModalSK';
        },
        tilesModalComponent() {
            return this.themeParams?.['tilesModalComponent'] || 'OTilesModal';
        },
        smsModalComponent() {
            return this.themeParams?.['smsModalComponent'] || 'OSmsModalOtg';
        },
        isDataAutoUpdatesEnabled(){
            return this.dataSourceType === 'CDN' && this.getMapConfigProp('features.data_sync.enabled', false);
        },
        isPagesKeepAliveCacheEnabled(){
            return this.getMapConfigProp('features.system.terminal_pages_keep_alive_cache', true);
        },
        computedGettersPreloadingTrigger(){
            return this.getBrandsAZ.length &&
                this.getBrandsRoot.length &&
                this.getCategoriesAZ.length &&
                this.getServiceCategoriesAZ.length &&
                this.getTenantCategoriesRoot.length &&
                this.getTenantRootCategoryChildren.length
        },
        themeModals() {
            return Object.keys(this.getMapConfigProp('theme_params.modals', {}));
        },
        shouldShowDebugPanel(){
            return document.location.href.includes('showDebugPanel=1');
        },
        isLocalHost(){
            return location.origin.includes('192.168.') ||
                location.origin.includes('localhost') ||
                location.origin.includes('127.0.0.1')
        },
    },
    created() {
        this.initEventListeners();
        Sentry.setTag("app-type", APP_TYPES.TERMINAL);
        Sentry.setTag("is-elo", this.isElo);
    },
    async mounted() {
        !this.$route.query.terminalMode && fitToScreen();
        this.setTerminalCode(this.terminalCode);
        await this.fetchMapData(this.mapId);

        this.setPlayerUrl(this.$route.query.playerUrl);

        if(this.isDataAutoUpdatesEnabled){
            this.mapDataUpdateCheckInterval = setInterval(
                () => this.checkDoesMapDataUpdateNeeded(this.mapId),
                this.getMapConfigProp('features.data_sync.check_interval', INTERVAL_CHECK_MAP_DATA_MODIFIED) * 1000
            );
        }
        this.moviesListInterval();
        window.addEventListener('online', this.getMoviesList);
    },
    beforeDestroy() {
        this.deactivateEventListeners();
        clearTimeout(this.activeTimeout);
        clearInterval(this.moviesListTimeout);
        clearInterval(this.setAppActiveWithDelayTimeoutId);
        clearTimeout(this.delayUpdateTerminalDataTimeout);
        clearInterval(this.mapDataUpdateCheckInterval);
        window.removeEventListener('online', this.getMoviesList);
    },
    watch: {
        mapLoading(value) {
            if (value) this.disableGyroscope();
            this.$moment.locale(this.getActiveLocale === 'se' ? 'sv' : this.getActiveLocale);

        },
        canUpdateMapData(value) {
            if  (value) {
                this.maybeUpdateMapData()
                /* this.pushMapObjects(this.unfilteredMapObjects)
                this.checkMapObjectsInterval = setInterval(() => {
                    this.pushMapObjects(this.unfilteredMapObjects)
                }, INTERVAL_CHECK_MAP_OBJECTS)*/
            } else {
                clearInterval(this.checkMapObjectsInterval)
            }
        },
        isAppLoaded(value) {
            if (value) document.title = this.map.name;
        },
        isMapDataUpdateNeeded: 'maybeUpdateMapData',
    },
    methods: {
        ...mapActions([
            'fetchMapData',
            'getMoviesList',
            'generateSessionId',
            'moviesListInterval',
            'checkDoesMapDataUpdateNeeded'
        ]),
        ...mapMutations([
            'setMapLoading',
            'setPlayerUrl',
            'pushMapObjects',
            'setTerminalCode',
            'setCanUpdateMapData',
            'setIsMapDataUpdateNeeded',
        ]),
        initEventListeners() {
            this.$bus.$on(SHOW_MODAL, this.showModal);
            this.$bus.$on(SHOW_OVERLAY, this.showOverlay);
            this.$bus.$on(UI_MAP_CLICKED, this.setAppActive);
            this.$bus.$on(SESSION_EXTENDED,this.setAppActive);
            this.$bus.$on(UI_MAP_READY, this.mapReadyActions);
            this.$bus.$on(SESSION_TERMINATED, this.turnOnPlayerWithDelay);

            document.addEventListener("touchend", this.setAppActiveWithDelay, {passive: true});
            document.addEventListener("wheel", this.setAppActiveWithDelay, {passive: true});
        },
        deactivateEventListeners() {
            this.$bus.$off(SHOW_MODAL, this.showModal);
            this.$bus.$off(SHOW_OVERLAY, this.showOverlay);
            this.$bus.$off(UI_MAP_CLICKED, this.setAppActive);
            this.$bus.$off(SESSION_EXTENDED,this.setAppActive);
            this.$bus.$off(UI_MAP_READY, this.mapReadyActions);
            this.$bus.$off(SESSION_TERMINATED, this.turnOnPlayerWithDelay);

            document.removeEventListener("touchend", this.setAppActiveWithDelay);
            document.removeEventListener("wheel", this.setAppActiveWithDelay);
        },

        maybeUpdateMapData(){
            this.canUpdateMapData &&
                this.isMapDataUpdateNeeded &&
                this.dataSourceType === 'CDN' &&
                this.fetchMapData(this.mapId);
        },
        showModal(data) {
            this.modalOrganism = data;
        },
        closeModal() {
            this.modalOrganism = null;
            this.$bus.$emit(EVENTS.MODALS.OFF_MODAL, false);
            this.$bus.$emit(EVENTS.MODALS.OFF_INFO_MODAL, false);

        },
        showOverlay(val) {
            this.isOverlay = val;
        },
        turnOnPlayerWithDelay() {
            this.screensaverTimeout = setTimeout(()=> {
                this.turnOnPlayer();
            }, this.screensaverTimer*1000 || DELAY_BEFORE_PLAYER_START)
        },
        turnOnPlayer() {
            this.closeModal();
            this.$bus.$emit(RESTORE_LOCALE, true);
            this.player = true;
            clearTimeout(this.activeTimeout);
            this.delayUpdateTerminalData();
        },
        handlePlayerClick() {
            this.player = false;
            this.setAppActive();
            clearTimeout(this.delayUpdateTerminalDataTimeout);
            this.setCanUpdateMapData(false)
        },
        setAppActiveWithDelay(){
            if(!this.setAppActiveWithDelayTimeoutId){
                this.setAppActiveWithDelayTimeoutId = setTimeout( () => this.setAppActive(), 500);
            }
        },
        setAppActive() {
            this.player = false;
            clearTimeout(this.activeTimeout);
            clearTimeout(this.screensaverTimeout);
            this.activeTimeout = setTimeout(this.timeoutAction, this.resetInterval * 1000 || RESET_TERMINAL_DATA);
        },
        timeoutAction() {
            if (this.$route.name === START_SCREEN) {
                this.turnOnPlayer();
                clearTimeout(this.activeTimeout);
                this.generateSessionId()
            } else {
                this.modalOrganism = {
                    component: 'notification'
                };
            }
        },
        mapReadyActions() {
            this.setAppActive();
            this.disableGyroscope();

            document.viewApi && document.viewApi.register_event('on_camera_update', this.setAppActive);

            if(!this.$route.name.includes('guide'))
                this.$bus.$emit(STOP_VIEW_API);

        },
        disableGyroscope() {
            this.$bus.$emit(SET_GYROSCOPE_STATE, false);
        },
        delayUpdateTerminalData() {
            this.delayUpdateTerminalDataTimeout =  setTimeout(() => {
                if(this.player) this.setCanUpdateMapData(true)
            }, DELAY_BEFORE_CAN_UPDATE_TEMINAL_DATA*1000)
        }
    }
}
</script>

<style lang="scss" src="./p-terminal.scss" />
